import type { CSSProperties } from 'react';

import { resolveCosmeticAsset } from '../profile-assets.js';
import { SIDE_DECORATION_METRICS } from '../side-decoration-metrics.generated.js';

/**
 * The decorative image at the far-right of the profile hero (APP_SPEC §13.2)
 * — its own replaceable cosmetic layer, never baked into the hero
 * background.
 *
 * A side decoration is an L-shaped corner ornament: a gilt frame rail runs
 * down its right side and along its bottom, and foliage on the art spills
 * *past* that rail on purpose. It is placed so the rail sits on the hero's
 * brass frame (the two read as one border at that corner) while the foliage
 * hangs over the edge. `pnpm assets:decorations` measures where each master's
 * rail is — `SIDE_DECORATION_METRICS` — so there are no per-art magic numbers
 * here.
 *
 * Positioned absolutely against the hero, so it never dictates the hero's
 * height or pushes the identity / Overall content around (the hero grid just
 * reserves right padding for it) — but it does *scale with* the hero, so a
 * taller hero at a narrower desktop width gets a taller decoration rather
 * than the same art stranded in a bigger panel. Every dimension is derived:
 * the height from the hero's own height and the art's measured rail inset,
 * the width from the art's aspect ratio, and the cap from the gutter the hero
 * publishes — there is no hard-coded hero height anywhere in here to go stale
 * the next time the hero's proportions change. Purely decorative: `pointer-events:none`,
 * `aria-hidden`, hidden below the desktop breakpoint where there is no room
 * for it (docs/UI_GUIDELINES.md §7). Renders nothing when no decoration is
 * selected.
 */
export interface ProfileSideDecorationProps {
  /** Selected side-decoration cosmetic id, or `null` for none. */
  decorationId: string | null;
}

/**
 * How far the art may overhang the top of the hero, and the fallback border
 * width for a hero rendered without an ornamental frame.
 */
const HERO_FRAME_PX = 5;
/**
 * The hero publishes the horizontal space it has reserved for this art as
 * `--px-hero-decoration-gutter` (see `ProfileHero`). That variable — not a
 * frozen pixel height — is what caps the decoration, so the two stay in step
 * when the hero's proportions change. The fallback matches the hero's own
 * desktop default for a decoration rendered outside one.
 */
const GUTTER_VAR = 'var(--px-hero-decoration-gutter, 118px)';

export function ProfileSideDecoration({ decorationId }: ProfileSideDecorationProps) {
  if (!decorationId) return null;

  const metrics = SIDE_DECORATION_METRICS[decorationId];

  let style: CSSProperties;
  if (metrics) {
    // Height tracks the hero and `aspect-ratio` derives the width from it, so
    // the art rescales with the hero instead of sitting at a frozen size
    // inside a taller panel. The rail is then seated on the middle of the
    // brass frame by translating the image by its *own* measured rail insets
    // — percentage transforms resolve against the element, so this stays
    // correct at every rendered size. Foliage between the rail and the art's
    // edge overhangs, which is the intent.
    //
    // The height is solved for the *translated* box, not the untranslated one.
    // The element is pushed down by its own `railInsetBottom`, so a plain
    // `100% + overhang` box ends up starting that far below the hero's top
    // edge — a blank strip above the art, and an equal length of it hanging
    // detached below the hero. Dividing by `1 - railInsetBottom` is what makes
    // the art span the hero however tall the hero grows.
    const spanningHeight = `calc((100% + ${String(HERO_FRAME_PX)}px) / ${String(
      1 - metrics.railInsetBottom,
    )})`;
    // The height at which the art's in-hero width (everything left of its own
    // gilt rail's overhang) exactly fills the reserved gutter. Beyond this the
    // decoration would start eating into the Overall bay, so it is the cap.
    const gutterLimitedHeight = `calc(${GUTTER_VAR} / ${String(
      metrics.aspectRatio * (1 - metrics.railInsetRight),
    )})`;

    style = {
      height: `min(${spanningHeight}, ${gutterLimitedHeight})`,
      aspectRatio: String(metrics.aspectRatio),
      // Seated on the *centre line* of the hero's ornamental rail, which the
      // frame publishes as `--px-frame-rail-*`. Hard-coded offsets put the
      // art's own gilt rail where the old thin 9-slice border used to be —
      // outside the brass entirely — so it read as a second border hanging
      // past the hero, with its base below the hero's bottom rail. The
      // translate underneath still does the rest: it pushes the image by its
      // own measured rail inset, landing that rail on this line.
      right: 'var(--px-frame-rail-inline, -2.5px)',
      bottom: 'var(--px-frame-rail-block, 0px)',
      transform: `translate(${String(metrics.railInsetRight * 100)}%, ${String(
        metrics.railInsetBottom * 100,
      )}%)`,
    };
  } else {
    // No measured art yet (id still on the structural placeholder): a plain
    // right-aligned image tucked just inside the frame.
    style = {
      height: `min(calc(100% - 8px), calc(${GUTTER_VAR} / 0.5))`,
      aspectRatio: '0.5',
      right: 4,
      bottom: 4,
    };
  }

  return (
    <img
      src={resolveCosmeticAsset('sideDecoration', decorationId)}
      alt=""
      aria-hidden="true"
      draggable={false}
      // Dimensions are inline, not Tailwind: every value in `style` is
      // computed above from the decoration's own measured rail geometry
      // (`side-decoration-metrics.generated.ts`), so there is no utility class
      // to express them.
      style={style}
      className="pointer-events-none absolute z-0 hidden select-none object-contain object-right-bottom [filter:drop-shadow(0_3px_10px_rgb(4_31_29/0.35))] desktop:block"
    />
  );
}
